import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { parseGiftText, MAX_CHARS } from './parseGiftText';
import { normalizeUrl, normKey, hostOf, INVALID_LINK_MESSAGE } from './giftText';

const TAP = { minHeight: 44 };

function useWide() {
  const query = '(min-width: 700px)';
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return wide;
}

let rowSeq = 0;

// `person`: whose list this is (ignored wording-wise for your own list).
// Smart Add: paste text -> AI builds rows -> review -> save.
// `existingTitles`: titles already on the target list (for duplicate flags).
// `onSave(rows)`: rows are { title, notes, link, claim }; must return a promise.
export default function SmartAddSheet({ person, isOwnList, existingTitles, onSave, onClose }) {
  const wide = useWide();
  const [stage, setStage] = useState('input'); // input | loading | preview
  const [raw, setRaw] = useState('');
  const [message, setMessage] = useState('');
  const [rows, setRows] = useState([]);
  const [fallback, setFallback] = useState(false);
  const [claimAll, setClaimAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const job = useRef(0);
  const opener = useRef(document.activeElement);
  const textRef = useRef(null);
  const targetName = isOwnList ? 'your wishlist' : `${person}’s list`;

  useEffect(() => {
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const openerEl = opener.current;
    return () => {
      // Drop any in-flight parse result once the sheet is gone.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      job.current++;
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      if (openerEl && openerEl.focus) openerEl.focus();
    };
  }, [onClose]);

  useEffect(() => {
    if (stage === 'input' && textRef.current) textRef.current.focus();
  }, [stage]);

  const tooLong = raw.length > MAX_CHARS;

  const sortItOut = async () => {
    if (!raw.trim() || tooLong) return;
    const mine = ++job.current;
    setMessage('');
    setStage('loading');
    const { items, fallback: usedFallback } = await parseGiftText(raw);
    if (mine !== job.current) return;
    if (!items.length) {
      setMessage('We couldn’t find any gifts in that. Try pasting the list again, or add gifts one at a time.');
      setStage('input');
      return;
    }
    const existing = new Set(existingTitles.map(normKey));
    setFallback(usedFallback);
    setClaimAll(false);
    setRows(items.map(it => {
      const flag = existing.has(normKey(it.title)) ? 'dup' : (it.maybe ? 'maybe' : null);
      return { id: 'r' + (++rowSeq), title: it.title, notes: it.notes || '', link: it.link || '', checked: !flag, claim: false, open: false, flag, err: '' };
    }));
    setStage('preview');
  };

  const patchRow = (id, patch) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const selected = rows.filter(r => r.checked);

  const toggleClaimAll = (on) => {
    setClaimAll(on);
    setRows(rs => rs.map(r => (r.checked ? { ...r, claim: on } : r)));
  };

  const removeRow = (id) => {
    const left = rows.filter(r => r.id !== id);
    setRows(left);
    if (!left.length) {
      setMessage('Every row was removed. Paste again to start over.');
      setStage('input');
    }
  };

  const save = async () => {
    if (!selected.length || saving) return;
    let firstBad = null;
    const checked = rows.map(r => {
      if (!r.checked) return r;
      let err = '';
      let link = r.link;
      let open = r.open;
      if (!r.title.trim()) err = 'Add a title, or uncheck this row.';
      else if (r.link.trim()) {
        const url = normalizeUrl(r.link);
        if (url) link = url;
        else { err = INVALID_LINK_MESSAGE; open = true; }
      }
      if (err && !firstBad) firstBad = r.id;
      return { ...r, err, link, open };
    });
    setRows(checked);
    if (firstBad) {
      setTimeout(() => {
        const el = document.querySelector(`[data-row="${firstBad}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' });
      }, 0);
      return;
    }
    setSaving(true);
    try {
      await onSave(checked.filter(r => r.checked).map(r => ({
        title: r.title.trim(), notes: r.notes.trim(), link: r.link.trim(), claim: !isOwnList && r.claim
      })));
    } catch (error) {
      console.error('Smart Add save failed:', error);
      setMessage('Something went wrong saving those. Nothing was added. Please try again.');
      setSaving(false);
    }
  };

  const panelStyle = wide
    ? { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 600, maxHeight: '86vh', borderRadius: 18 }
    : { left: 0, right: 0, bottom: 0, maxHeight: '92vh', borderRadius: '18px 18px 0 0' };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="smart-add-title"
        className="fixed z-50 flex flex-col bg-white shadow-2xl text-gray-800"
        style={panelStyle}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
          <h2 id="smart-add-title" className="text-lg font-bold min-w-0 break-words">Smart Add to {targetName}</h2>
          <button onClick={onClose} aria-label="Close" className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ minHeight: 0, overscrollBehavior: 'contain' }}>
          {stage === 'input' && (
            <>
              <p id="smart-add-lead" className="text-base">Paste one gift or a whole list, in any format, and we’ll figure it out.</p>
              {message && <p role="alert" className="text-sm text-red-600">{message}</p>}
              <textarea
                ref={textRef}
                aria-labelledby="smart-add-lead"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder="A gift idea, a link, a shared note, a text thread, an email. Anything goes."
                className="w-full p-3 border border-gray-300 rounded-xl text-base"
                style={{ minHeight: 200, resize: 'vertical' }}
              />
              {tooLong && (
                <p role="alert" className="text-sm text-red-600">
                  That’s {raw.length.toLocaleString()} characters. The limit is {MAX_CHARS.toLocaleString()}. Paste a shorter chunk, or do it in two goes.
                </p>
              )}
              <p className="text-xs text-gray-500">Pasted text is processed by AI to build this list. Leave out anything private.</p>
            </>
          )}

          {stage === 'loading' && (
            <div role="status" className="flex flex-col items-center gap-3 py-12 text-gray-500">
              <span className="animate-spin rounded-full border-4 border-gray-200 border-t-green-600" style={{ width: 30, height: 30 }} aria-hidden="true" />
              <p>Reading your list…</p>
            </div>
          )}

          {stage === 'preview' && (
            <>
              {fallback && (
                <div role="status" className="p-3 rounded-lg bg-yellow-100 text-yellow-900 text-sm">
                  The AI couldn’t read this one, so we split it by line instead. Check each row before adding.
                </div>
              )}
              {message && <p role="alert" className="text-sm text-red-600">{message}</p>}
              <p className="text-base">
                Found <b>{rows.length}</b> {rows.length === 1 ? 'gift' : 'gifts'}. Uncheck anything that isn’t a gift, fix the wording, then add.
              </p>
              {!isOwnList && (
                <label className="flex items-start gap-3 p-3 rounded-xl border border-blue-200 bg-blue-50 cursor-pointer">
                  <input type="checkbox" checked={claimAll} onChange={(e) => toggleClaimAll(e.target.checked)} className="mt-1" style={{ width: 22, height: 22 }} />
                  <span className="flex flex-col min-w-0">
                    <b>I’m getting these</b>
                    <small className="text-gray-600">Marks every checked gift as claimed by you so nobody doubles up. You can still change single rows.</small>
                  </span>
                </label>
              )}
              <ul className="divide-y divide-gray-200">
                {rows.map(r => (
                  <li key={r.id} data-row={r.id} className="flex items-start gap-1.5 py-3">
                    <label className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 44 }}>
                      <input
                        type="checkbox"
                        checked={r.checked}
                        aria-label="Include this gift"
                        onChange={(e) => patchRow(r.id, { checked: e.target.checked, ...(e.target.checked && claimAll && !isOwnList ? { claim: true } : {}) })}
                        style={{ width: 22, height: 22 }}
                      />
                    </label>
                    <div className="flex-1 min-w-0 space-y-1.5" style={{ opacity: r.checked ? 1 : 0.55 }}>
                      <input
                        type="text"
                        value={r.title}
                        aria-label="Gift title"
                        autoComplete="off"
                        onChange={(e) => patchRow(r.id, { title: e.target.value, err: '' })}
                        className="w-full p-2 border border-gray-300 rounded-lg font-semibold"
                        style={TAP}
                      />
                      {r.flag && (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-900 text-xs font-semibold">
                          {r.flag === 'dup' ? `Already on ${targetName}` : 'May not be a gift'}
                        </span>
                      )}
                      {!r.open && (r.notes || r.link) && (
                        <p className="text-sm text-gray-600 break-words">{[r.notes, r.link ? hostOf(r.link) : ''].filter(Boolean).join(' · ')}</p>
                      )}
                      {r.open && (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={r.notes}
                            placeholder="Notes (optional)"
                            aria-label="Notes"
                            onChange={(e) => patchRow(r.id, { notes: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            style={TAP}
                          />
                          <input
                            type="text"
                            value={r.link}
                            placeholder="Link (optional)"
                            aria-label="Link"
                            inputMode="url"
                            autoCapitalize="off"
                            autoComplete="off"
                            onChange={(e) => patchRow(r.id, { link: e.target.value, err: '' })}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            style={TAP}
                          />
                        </div>
                      )}
                      {r.err && <p role="alert" className="text-sm text-red-600 break-words">{r.err}</p>}
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => patchRow(r.id, { open: !r.open })} className="px-3 text-sm border border-gray-300 rounded-lg hover:bg-gray-50" style={{ minHeight: 36 }}>
                          {r.open ? 'Done' : 'Edit details'}
                        </button>
                        {!isOwnList && (
                          <button
                            type="button"
                            aria-pressed={r.claim}
                            onClick={() => patchRow(r.id, { claim: !r.claim })}
                            className={`px-3 text-sm border rounded-lg ${r.claim ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300 hover:bg-gray-50'}`}
                            style={{ minHeight: 36 }}
                          >
                            I’m getting this
                          </button>
                        )}
                      </div>
                    </div>
                    <button type="button" onClick={() => removeRow(r.id)} aria-label="Remove this row" className="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100">
                      <X className="w-5 h-5" />
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500">
                {isOwnList
                  ? 'These go on your wishlist for the whole family to see and claim.'
                  : `These are added as custom gifts. Only you can edit or remove them, and ${person} won’t see them.`}
              </p>
            </>
          )}
        </div>

        <div className="flex gap-2 px-4 pt-3 border-t" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
          {stage === 'input' && (
            <>
              <button onClick={sortItOut} disabled={!raw.trim() || tooLong} className="flex-1 px-4 rounded-lg bg-green-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed" style={TAP}>
                <Sparkles className="w-5 h-5" /> Sort it out
              </button>
              <button onClick={onClose} className="px-4 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50" style={TAP}>Cancel</button>
            </>
          )}
          {stage === 'loading' && (
            <button onClick={() => { job.current++; setStage('input'); }} className="flex-1 px-4 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50" style={TAP}>Cancel</button>
          )}
          {stage === 'preview' && (
            <>
              <button onClick={save} disabled={!selected.length || saving} className="flex-1 px-4 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed" style={TAP}>
                {saving ? 'Adding…' : selected.length ? `Add ${selected.length} ${selected.length === 1 ? 'gift' : 'gifts'}` : 'Select gifts to add'}
              </button>
              <button onClick={() => { setMessage(''); setStage('input'); }} disabled={saving} className="px-4 rounded-lg border border-gray-300 font-semibold hover:bg-gray-50 disabled:opacity-50" style={TAP}>Start over</button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
